import {
  formatDateInputValueForTimeZone,
  formatDateTimeInputValueForTimeZone,
} from '@/lib/time-zone';
import type { ContentModelMetadataConfig } from '@/types/shared';
import { isPendingTagId } from './pending-references';
import type { MetadataDialogKind, ResourceTagType, TagOption } from './types';

export type MetadataFieldConfig = {
  title: string;
  label: string;
  tagType: ResourceTagType;
};

export type MetadataFieldConfigWithLimits = MetadataFieldConfig & {
  maxSelected?: number;
  maxLength?: number;
};

export const METADATA_FIELD_CONFIG: Record<MetadataDialogKind, MetadataFieldConfig> = {
  author: {
    title: 'Authors',
    label: 'Author',
    tagType: 'profile' as const,
  },
  topics: {
    title: 'Topics',
    label: 'Topic',
    tagType: 'topic' as const,
  },
  attorney: {
    title: 'Attorneys',
    label: 'Attorney',
    tagType: 'profile' as const,
  },
  courts: {
    title: 'Courts',
    label: 'Court',
    tagType: 'court' as const,
  },
  practiceAreas: {
    title: 'Practice Areas',
    label: 'Practice Area',
    tagType: 'practice_area' as const,
  },
};

export function getMetadataFieldConfig(
  kind: MetadataDialogKind,
  contentModelMetadataConfig?: ContentModelMetadataConfig,
): MetadataFieldConfigWithLimits {
  return {
    ...METADATA_FIELD_CONFIG[kind],
    ...(contentModelMetadataConfig?.fields[kind] || {}),
  };
}

export function toDateTimeLocalValue(value?: string) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

export function toDateInputValue(value?: string, timeZone?: string | null) {
  if (value && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;

  if (value && timeZone) return formatDateInputValueForTimeZone(value, timeZone);

  const normalized = toDateTimeLocalValue(value);
  if (!normalized) return '';

  const [date = ''] = normalized.split('T');
  return date;
}

export function toDateTimeInputValue(value?: string, timeZone?: string | null) {
  if (!value) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return `${value}T00:00`;
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)) return value;

  if (timeZone) return formatDateTimeInputValueForTimeZone(value, timeZone);

  return toDateTimeLocalValue(value);
}

// A pending member keeps its name and carries a null id until its proposal is accepted.
export type SelectedMember = { id: string | null; name: string };

export function buildSelectedOptions(
  options: TagOption[],
  selectedIds: string[],
): SelectedMember[] {
  return selectedIds.flatMap<SelectedMember>((id) => {
    const option = options.find((candidate) => candidate.id === id);
    if (!option) return [];

    return [isPendingTagId(id) ? { id: null, name: option.name } : option];
  });
}

export function sortTagsByName(tags: TagOption[]) {
  return [...tags].sort((a, b) => a.name.localeCompare(b.name));
}

export function extractTagErrorMessage(error: unknown, fallback: string) {
  return extractMetadataErrorMessage(error, fallback);
}

export function extractMetadataErrorMessage(error: unknown, fallback: string) {
  const responseData = (error as { response?: { data?: unknown } })?.response?.data as
    | {
        error?: unknown;
        errors?: unknown;
      }
    | undefined;
  const responseErrors = responseData?.errors;
  const responseError = responseData?.error;

  if (typeof responseErrors === 'string' && responseErrors.trim().length > 0) {
    return responseErrors;
  }

  if (Array.isArray(responseErrors)) {
    const message = responseErrors
      .filter((item) => typeof item === 'string' && item.trim().length > 0)
      .join(', ');
    if (message.length > 0) return message;
  }

  if (responseErrors && typeof responseErrors === 'object') {
    const message = Object.values(responseErrors)
      .flatMap((value) => (Array.isArray(value) ? value : [value]))
      .filter((item) => typeof item === 'string' && item.trim().length > 0)
      .join(', ');
    if (message.length > 0) return message;
  }

  if (typeof responseError === 'string' && responseError.trim().length > 0) {
    return responseError;
  }

  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }

  return fallback;
}
